import { ApplicationElement, html } from '@beunity/lit-components';
import { ifDefined } from 'lit/directives/if-defined.js';

import style from './style.lit.css';
import translations from './translations.js';

import CommonActions from '@beunity/lit-components/components/common_actions/element.js';
import '@beunity/lit-components/components/icon_block/element.js';
import '@beunity/lit-components/components/img_placeholder/element.js';
import '../icon/element.js';

/**
 * Component that links to a group or a group-joining page, with details about the group.
 *
 * @prop favoriteOn - Should show "favorite" action?
 * @prop favoriteValue - Is group among current users favorites (optional)
 * @prop favoriteUrl - Url for favorite action (optional)
 * @prop image - Should display a placeholder if no image (optional)
 * @prop membersCount - Number of members
 * @prop role - For displaying user's role in the group (options: member, writer, author, administrator, super_admin, owner, )
 * @prop type - Type of the current group (options: open, private, closed)
 * @prop url - Link to the Group show page (optional)
 * @prop name - Internal property => Will be derived from the innerText of the element and will be passed as alt text for image in image placeholder
 *
 * @slot title - for title
 * @slot actions - for additional actions next to favorite action (optional)
 */

export default class GroupItem1 extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    favoriteValue: { type: Boolean },
    favoriteOn: { type: Boolean },
    favoriteUrl: { type: String },
    image: { type: String },
    membersCount: { type: Number },
    role: { type: String },
    type: { type: String },
    url: { type: String },
    name: { state: true },
  };

  constructor() {
    super();

    /** @internal */
    this.commonActions = new CommonActions(this);
    this.favoriteValue = false;
  }

  /** @internal */
  typeIcons = {
    open: 'lock-open-alt',
    closed: 'lock-alt',
    private: 'user-lock',
  };

  /** @internal */
  typeNames = {
    open: this.t('type.open'),
    closed: this.t('type.closed'),
    private: this.t('type.private'),
  };

  /** @internal */
  roleNames = {
    member: this.t('role.member'),
    writer: this.t('role.writer'),
    author: this.t('role.author'),
    administrator: this.t('role.administrator'),
    super_admin: this.t('role.super_admin'),
    owner: this.t('role.owner'),
  };

  /** @internal */
  get typeIcon() {
    return this.typeIcons[`${this.type}`];
  }

  /** @internal */
  get typeName() {
    return this.typeNames[`${this.type}`];
  }

  /** @internal */
  get roleName() {
    return this.roleNames[`${this.role}`];
  }

  onSlotChange() {
    this.name = this.innerText;
  }

  async remoteToggleFavorite() {
    try {
      this.favoriteValue = !this.favoriteValue;
      await this.commonActions.post(this.favoriteUrl);
      this.commonActions.displaySnackBarSuccess(
        this.t(`snack.favorite.${this.favoriteValue ? 'on' : 'off'}`)
      );
    } catch {
      this.favoriteValue = !this.favoriteValue;
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  renderGroupImage() {
    return html`
      <ef-img-placeholder
        picture=${ifDefined(this.image)}
        alt=${ifDefined(this.name)}>
      </ef-img-placeholder>
    `;
  }

  renderFavorite() {
    return html`
      <button @click=${this.remoteToggleFavorite} class="bu-btn btn-naked-dark">
        <bu-icon name="${this.favoriteValue ? 'star:solid' : 'star'}"></bu-icon>
      </button>
    `;
  }

  renderRole() {
    return html`
      <ef-icon-block icon="user" text="${this.roleName}" class="role">
      </ef-icon-block>
    `;
  }

  renderContent() {
    return html`
      <main>
        ${this.renderGroupImage()}
        <div class="content">
          <slot name="title" @slotchange=${this.onSlotChange}></slot>

          <div class="info">
            <ef-icon-block
              icon="${this.typeIcon}"
              text="${this.typeName}"
              class="type">
            </ef-icon-block>
            <span class="members">
              ${this.t('members_count', { count: this.membersCount })}
            </span>
          </div>
          ${this.roleName ? this.renderRole() : null}
        </div>
      </main>
    `;
  }

  renderContentWithLink() {
    return html` <a href="${this.url}">${this.renderContent()}</a> `;
  }

  render() {
    return html`
      ${this.url ? this.renderContentWithLink() : this.renderContent()}
      <aside>
        ${this.favoriteOn ? this.renderFavorite() : null}
        <slot name="actions"></slot>
      </aside>
    `;
  }
}

GroupItem1.registerTagName('bu-group-item-1');

customElements.get('bu-group-item-1') ||
  customElements.define('bu-group-item-1', GroupItem1);
